import React, { useCallback, useContext, useMemo } from 'react'
import { ApolloClient, InMemoryCache, ApolloProvider as ApProvider, concat, HttpLink, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { useFlag } from '@unleash/proxy-client-react'
import { GraphQLError } from 'graphql'
import { isNil } from 'lodash'
import { useHistory, useLocation } from 'react-router-dom'
import { EMPTY_STRING, EPIC_4335_BUG_5895, OPERATIONAL_401_UNAUTHORIZED } from '@mth/constants'
import { MthRoute, QUERY_PARAMETERS } from '@mth/enums'
import { isTokenExpired, logoutUser } from '@mth/services/jwt-session/jwt-session.service'
import { AuthContext } from '../AuthProvider/AuthContext'
import { UserContext } from '../UserContext/UserProvider'

const isUnauthenticatedRoute = (path: string): boolean => {
  const unauthenticatedRoutes = [
    MthRoute.DASHBOARD, // Login page
    MthRoute.APPLICATIONS,
    MthRoute.CONFIRM_EMAIL,
    MthRoute.EMAIL_VERIFICATION,
    MthRoute.FORGOT_PASSWORD,
    MthRoute.RESET_PASSWORD,
  ]

  return unauthenticatedRoutes.includes(path as MthRoute)
}

type ApolloProviderProps = {
  children: React.ReactNode
}

export const ApolloProvider: React.FC<ApolloProviderProps> = ({ children }) => {
  const { credentials, signOut, setCredentials } = useContext(AuthContext)
  const history = useHistory()
  const { setMe } = useContext(UserContext)
  const location = useLocation()
  const currentPath = location.pathname
  const epic4335Bug5895 = useFlag(EPIC_4335_BUG_5895)
  const operational401unauthorized = useFlag(OPERATIONAL_401_UNAUTHORIZED)

  const logout = async (noExpiration = false) => {
    await logoutUser()
    setMe(null)
    signOut()

    history.push({
      pathname: MthRoute.DASHBOARD,
      ...(noExpiration
        ? {}
        : { search: new URLSearchParams({ [QUERY_PARAMETERS.EXPIRATION]: currentPath }).toString() }),
    })
  }

  const logoutMemoized = useCallback(logout, [setMe, signOut, history, currentPath])

  const getToken = () => {
    if (typeof credentials === 'string') return credentials
    if (!isNil(localStorage.getItem('masquerade'))) return localStorage.getItem('masquerade')
    return localStorage.getItem('JWT')
  }

  const getTokenMemoized = useCallback(getToken, [credentials])

  const authLink = setContext(async (_, { headers }) => {
    const token = getToken()
    const residualCall = isResidualCall(_.operationName ?? EMPTY_STRING)
    if (!isUnauthenticatedRoute(currentPath) && !residualCall && (!token || isTokenExpired(token))) {
      await logout()
      throw new Error('Token expired: preventing GraphQL request')
    }
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    }
  })

  const authLinkMemoized = useMemo(() => authLink, [setCredentials, logoutMemoized, getTokenMemoized])

  const httpLink = new HttpLink({ uri: import.meta.env.VITE_PUBLIC_API_URL })

  const httpLinkMemoized = useMemo(() => httpLink, [])

  const isResidualCall = (operationName: string) => operationName === 'Me' || operationName === 'Schoolyear_getcurrent'

  const createApolloLink = () =>
    ApolloLink.from([
      onError(({ graphQLErrors }) => {
        if (graphQLErrors) {
          for (const err of graphQLErrors as GraphQLError[]) {
            if (err.extensions?.exception?.status === 401) {
              // prevent to show the expiration if is calling me
              // it prevent to show the expiration message when the users logout
              // usually “me” is called with other queries and those queries are where the 401 error will be caught.
              const isCallingMe = err.extensions?.exception?.path?.includes('me') || false

              // Skip logout for unauthenticated routes
              if (isUnauthenticatedRoute(currentPath)) {
                return
              }

              void logout(isCallingMe)
            }
          }
        }
      }),
      authLink,
      httpLink,
    ])

  const client = new ApolloClient({
    cache: new InMemoryCache({ addTypename: false }),
    link: operational401unauthorized ? createApolloLink() : concat(authLink, httpLink),
  })

  const clientMemoized = useMemo(() => client, [authLinkMemoized, httpLinkMemoized])

  return <ApProvider client={epic4335Bug5895 ? clientMemoized : client}>{children}</ApProvider>
}
